import { defineStore } from 'pinia'
import api from '@/services/api'
import { type User, type Session, type ApiError } from '@/types'

interface LoginResponse {
  user: User
  access_token: string
}

interface RefreshResponse {
  access_token: string
}

export const useAuthStore = defineStore('auth', {
  state: (): Session => ({
    user: null,
    accessToken: null,
    refreshPromise: null,
  }),

  getters: {
    isAuthenticated: (state) => !!state.accessToken,
    getUser: (state) => state.user,
  },

  actions: {
    async login(email: string, password: string): Promise<void> {
      try {
        const response = await api.post<LoginResponse>('/auth/login', { email, password })
        this.accessToken = response.access_token
        localStorage.setItem('auth_initialised', 'true')
      } catch {
        this.$reset()
        throw new Error('Login failed')
      }
    },

    async logout(): Promise<void> {
      try {
        await api.post('/auth/logout')
        this.accessToken = null
        this.user = null
      } finally {
        this.$reset()
        localStorage.removeItem('auth_initialised')
      }
    },

    async refreshToken(): Promise<string | null> {
      if (this.refreshPromise) {
        return this.refreshPromise
      }
      this.refreshPromise = (async () => {
        try {
          const response = await api.post<RefreshResponse>('/auth/refresh')
          this.accessToken = response.access_token
          return this.accessToken
        } catch {
          this.$reset()
          throw new Error('Token refresh failed')
        } finally {
          this.refreshPromise = null
        }
      })()

      return this.refreshPromise
    },

    async fetchUser(): Promise<void> {
      try {
        const response = await api.get<User>('/auth/me')
        this.user = response
      } catch {
        this.$reset()
        throw new Error('Fetch user failed')
      }
    },

    async initialize(): Promise<void> {
      if (this.isAuthenticated) {
        return
      }
      try {
        const hasAuthInitialised = localStorage.getItem('auth_initialised')
        if (hasAuthInitialised) {
          await this.refreshToken()
          if (this.accessToken) {
            await this.fetchUser()
          }
        }
      } catch {
        localStorage.removeItem('auth_initialised')
        this.$reset()
      }
    },

    async register(password: string, token: string): Promise<void> {
      try {
        await api.post<User>('/auth/activate', { token, password })
      } catch (error: unknown) {
        this.$reset()
        const err = error as ApiError
        throw new Error('Activation failed' + err.message)
      }
    },
  },
})
